// Sprint — Bulk Product Export (.xlsx)
//
// Shared utility used by both /admin/products and /admin/inventory. Fetches
// every product page (100 per request) + brands + categories + inventory in
// parallel, joins them client-side, then builds a single-sheet XLSX and
// triggers an immediate browser download.
//
// RBAC is enforced server-side by the `X-Store-Id` header (see AppShell +
// api.js): SUPER_ADMIN on the synthetic "platform" slug sees every product
// across stores; STORE_ADMIN sees only their store. No frontend gating needed.

import * as XLSX from "xlsx";
import { api } from "@/lib/api";

// Fetch every page of an endpoint that returns { items, total, page, limit }.
async function fetchAllPages(path, pageSize = 100) {
  const first = await api.get(`${path}${path.includes("?") ? "&" : "?"}page=1&limit=${pageSize}`);
  const total = first.data.total || (first.data.items || []).length;
  const items = [...(first.data.items || [])];
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return items;
  // Parallel fetch pages 2..N.
  const rest = await Promise.all(
    Array.from({ length: pages - 1 }).map((_, i) =>
      api.get(`${path}${path.includes("?") ? "&" : "?"}page=${i + 2}&limit=${pageSize}`).then((r) => r.data.items || []).catch(() => [])
    )
  );
  rest.forEach((chunk) => items.push(...chunk));
  return items;
}

export async function exportProductsToXLSX({ filename } = {}) {
  // Parallel fetch: products (all pages) + brands + categories + inventory (all pages).
  const [products, brandsRes, categoriesRes, inventory] = await Promise.all([
    fetchAllPages("/products", 100),
    api.get("/brands?limit=200").catch(() => ({ data: { items: [] } })),
    api.get("/categories?limit=200").catch(() => ({ data: { items: [] } })),
    fetchAllPages("/inventory", 100).catch(() => []),
  ]);

  const brandById = new Map((brandsRes.data.items || []).map((b) => [b.id, b]));
  const catById = new Map((categoriesRes.data.items || []).map((c) => [c.id, c]));
  // Inventory can have multiple rows per product (per store). Sum qty_on_hand.
  const stockByProduct = new Map();
  inventory.forEach((inv) => {
    const pid = inv.product_id;
    if (!pid) return;
    stockByProduct.set(pid, (stockByProduct.get(pid) || 0) + Number(inv.qty_on_hand || 0));
  });

  const rows = products.map((p) => ({
    "Product Name":   p.name || "",
    "SKU / Code":     p.sku || p.barcode || "",
    "Category":       catById.get(p.category_id)?.name || "",
    "Brand":          brandById.get(p.brand_id)?.name || "",
    "MRP":            Number(p.mrp || 0),
    "Selling Price":  Number(p.selling_price || p.price || 0),
    "Current Stock":  stockByProduct.get(p.id) ?? 0,
  }));

  const ws = XLSX.utils.json_to_sheet(rows, {
    header: ["Product Name", "SKU / Code", "Category", "Brand", "MRP", "Selling Price", "Current Stock"],
  });
  // Column widths for readability.
  ws["!cols"] = [
    { wch: 34 }, { wch: 18 }, { wch: 20 }, { wch: 20 },
    { wch: 10 }, { wch: 14 }, { wch: 14 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Products");

  const ts = new Date().toISOString().slice(0, 10);
  const name = filename || `products_export_${ts}.xlsx`;
  XLSX.writeFile(wb, name);

  return rows.length;
}
